import React, { useState } from "react";
import ImageAnnotator from "components/util/helpers/ImageAnnotator";
import { useRevisionFormContext } from "components/brief/reviewer/revision/RevisionForm.lib.context";
import View from "components/brief/reviewer/revision/content/mockups/RevisionMockups.view";

const RevisionMockups = ({ currentSection }) => {

  const {
    proposal,
    currentAuthor,
    isDeveloper,
    allowComments,
    activeMockupIndex,
    setActiveMockupIndex,
    updateSection,
  } = useRevisionFormContext();

  const sectionIndex = currentSection.sourceIndex;
  const section = proposal.content[sectionIndex] || {};
  const mockups = section.mockups || [];
  const currentMockup = mockups[activeMockupIndex] || {};
  const canNavigateMockups = mockups.length > 1;
  const [isFullscreenOpen, setIsFullscreenOpen] = useState(false);

  if (mockups.length === 0)
    return null;

  const visibleAnnotations = (section.annotations || [])
    .filter((annotation) => annotation.id)
    .filter((annotation) => isDeveloper || (annotation?.visibility_scope || "CLIENT") !== "DEV")
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  const annotationNumbers = {};
  visibleAnnotations.forEach((annotation, index) => {
    annotationNumbers[annotation.id] = index + 1;
  });

  const currentAnnotations = visibleAnnotations
    .filter((annotation) => annotation.type === "mockup" && annotation.target_idx === activeMockupIndex)
    .filter((annotation) => !annotation?.is_solved)
    .map((annotation) => ({
      geometry: annotation.position,
      data: {
        id: annotation.id,
        text: annotation.text,
        author: annotation.author,
        order: annotation.order,
        visibility_scope: annotation?.visibility_scope || "CLIENT",
        is_solved: !!annotation?.is_solved,
      },
    }));

  const onClickPrevMockup = () =>
    setActiveMockupIndex(activeMockupIndex - 1);

  const onClickNextMockup = () =>
    setActiveMockupIndex(activeMockupIndex + 1);

  const onClickOpenFullscreen = () =>
    setIsFullscreenOpen(true);

  const onClickCloseFullscreen = () =>
    setIsFullscreenOpen(false);

  const onChangeCurrentAnnotations = (imageAnnotatorAnnotations) => {
    if (!allowComments)
      return;

    updateSection(sectionIndex, (currentSectionData) => {
      const currentAnnotationsList = currentSectionData.annotations || [];
      const otherAnnotations = currentAnnotationsList.filter((annotation) => {
        const isCurrentMockupAnnotation = annotation.type === "mockup" && annotation.target_idx === activeMockupIndex;
        if (!isCurrentMockupAnnotation) return true;
        if (annotation?.is_solved) return true;
        return !(!isDeveloper && (annotation?.visibility_scope || "CLIENT") === "DEV");
      });
      const newMockupAnnotations = (imageAnnotatorAnnotations || []).map((annotation) => ({
        id: annotation.data.id,
        type: "mockup",
        visibility_scope: annotation?.data?.visibility_scope || "CLIENT",
        is_solved: !!annotation?.data?.is_solved,
        target_idx: activeMockupIndex,
        text: annotation.data.text,
        author: annotation.data.author || currentAuthor,
        order: annotation.data.order,
        position: annotation.geometry,
      }));

      return {
        ...currentSectionData,
        annotations: [...otherAnnotations, ...newMockupAnnotations],
      };
    });
  }

  return (
    <View
      mockups={mockups}
      activeMockupIdx={activeMockupIndex}
      canNavigateMockups={canNavigateMockups}
      currentMockup={currentMockup}
      currentAnnotations={currentAnnotations}
      annotationNumbers={annotationNumbers}
      allowComments={allowComments}
      currentAuthor={currentAuthor}
      isDeveloper={isDeveloper}
      ImageAnnotator={ImageAnnotator}
      isFullscreenOpen={isFullscreenOpen}
      onClickPrevMockup={onClickPrevMockup}
      onClickNextMockup={onClickNextMockup}
      onClickOpenFullscreen={onClickOpenFullscreen}
      onClickCloseFullscreen={onClickCloseFullscreen}
      onChangeCurrentAnnotations={onChangeCurrentAnnotations}
    />
  );

}

export default RevisionMockups;
